import { N8nModule } from '../../types';

export const module6: N8nModule = {
  id: 'module6',
  title: 'Real-World Projects',
  description: 'Apply n8n to solve real business problems with complete end-to-end workflows and learn from the community.',
  duration: '1 hour',
  objectives: [
    'Build complete automation solutions',
    'Implement e-commerce order processing',
    'Create social media scheduling workflows',
    'Automate customer support workflows',
    'Design data pipelines for analytics',
    'Leverage community resources and templates'
  ],
  content: `# Real-World Projects

## Project 1: E-commerce Order Processing

Automate the complete order fulfillment workflow from order received to customer notification.

### Features
- Order webhook receiver
- Inventory check and update
- Payment processing validation
- Shipping label generation
- Customer email notification
- Team alerts for high-value orders

## Project 2: Social Media Content Scheduler

Schedule and publish content across multiple social media platforms.

### Capabilities
- Content calendar integration
- Multi-platform posting (Twitter, LinkedIn, Facebook)
- Image optimization and resizing
- Post scheduling and queuing
- Analytics tracking
- Engagement monitoring

## Project 3: Customer Support Automation

Intelligent ticket routing and auto-response system.

### Components
- Email/form webhook intake
- Keyword-based categorization
- Priority assignment
- Team member assignment
- Auto-response for common questions
- Escalation workflows for urgent issues
- Integration with helpdesk tools (Zendesk, Freshdesk)

## Project 4: Data Pipeline for Analytics

ETL workflow for business intelligence and reporting.

### Pipeline Stages
- Extract data from multiple sources (APIs, databases, files)
- Transform and cleanse data
- Validate data quality
- Load into data warehouse
- Generate automated reports
- Alert on anomalies

## Optimization Best Practices

### Performance
- Use webhook triggers instead of polling
- Implement caching for frequently accessed data
- Batch operations when possible
- Optimize database queries

### Cost Efficiency
- Set appropriate execution timeouts
- Use queue mode for high-volume workflows
- Implement circuit breakers for expensive operations
- Monitor and optimize API usage

## Community Resources

### n8n Community
- Official forum and Discord
- Template marketplace
- Workflow sharing
- Community nodes
- Contributing to n8n open source

### Getting Help
- Documentation and guides
- Video tutorials
- GitHub issues and discussions
- Community workflows as learning resources
`,
  videoUrl: 'https://www.youtube.com/watch?v=example6',
  codeExamples: [
    {
      title: 'E-commerce Order Processing Workflow',
      explanation: 'Complete workflow for processing orders',
      code: `{
  "name": "E-commerce Order Processing",
  "nodes": [
    {
      "name": "Order Webhook",
      "type": "n8n-nodes-base.webhook",
      "parameters": {
        "path": "order-received",
        "authentication": "headerAuth"
      }
    },
    {
      "name": "Check Inventory",
      "type": "n8n-nodes-base.postgres",
      "parameters": {
        "operation": "executeQuery",
        "query": "SELECT quantity FROM inventory WHERE product_id = $1",
        "parameters": {
          "bindings": ["={{$json.productId}}"]
        }
      }
    },
    {
      "name": "Update Inventory",
      "type": "n8n-nodes-base.postgres",
      "parameters": {
        "operation": "executeQuery",
        "query": "UPDATE inventory SET quantity = quantity - $1 WHERE product_id = $2"
      }
    },
    {
      "name": "Send Confirmation Email",
      "type": "n8n-nodes-base.sendGrid"
    },
    {
      "name": "Notify Team",
      "type": "n8n-nodes-base.slack"
    }
  ]
}`,
      language: 'json'
    },
    {
      title: 'Social Media Scheduler',
      explanation: 'Multi-platform content posting workflow',
      code: `{
  "name": "Social Media Scheduler",
  "nodes": [
    {
      "name": "Schedule Trigger",
      "type": "n8n-nodes-base.scheduleTrigger",
      "parameters": {
        "rule": {
          "interval": [{
            "field": "hours",
            "hoursInterval": 2
          }]
        }
      }
    },
    {
      "name": "Get Scheduled Posts",
      "type": "n8n-nodes-base.airtable",
      "parameters": {
        "operation": "list",
        "filterByFormula": "AND(IS_SAME({Scheduled}, NOW(), 'hour'), {Posted} = FALSE())"
      }
    },
    {
      "name": "Post to Twitter",
      "type": "n8n-nodes-base.twitter"
    },
    {
      "name": "Post to LinkedIn",
      "type": "n8n-nodes-base.linkedin"
    },
    {
      "name": "Mark as Posted",
      "type": "n8n-nodes-base.airtable",
      "parameters": {
        "operation": "update",
        "updateKey": "id",
        "fieldsToUpdate": {
          "Posted": true,
          "PostedAt": "={{$now.toISOString()}}"
        }
      }
    }
  ]
}`,
      language: 'json'
    }
  ],
  practiceExercise: {
    title: 'Build Your Own Automation Project',
    description: 'Choose one of the four projects (e-commerce, social media, support, or data pipeline) and implement it from scratch with complete error handling, monitoring, and documentation.',
    requirements: [
      'Complete workflow implementation',
      'Error handling for all external services',
      'Monitoring and alerting',
      'Documentation of workflow logic',
      'Testing plan with sample data'
    ],
    hints: [
      'Start with a simple version and iterate',
      'Test each node independently before connecting',
      'Use pinned data for testing without external services'
    ]
  },
  quiz: {
    questions: [
      {
        id: 'q1-webhook-trigger',
        question: 'What is the recommended trigger type for receiving real-time data from external services?',
      options: ['Schedule Trigger', 'Webhook Trigger', 'Manual Trigger', 'Polling Trigger'],
      correctAnswer: 1,
      explanation: 'Webhook triggers provide real-time, event-driven execution and are more efficient than polling for real-time data.'
      },
      {
        id: 'q2-webhooks-vs-polling',
        question: 'Which approach is more cost-effective for checking API data updates?',
      options: [
        'Polling every minute',
        'Using webhooks when available',
        'Manual checks',
        'Checking once per day'
      ],
      correctAnswer: 1,
      explanation: 'Webhooks are push-based and only execute when there is actual data, whereas polling runs on a schedule regardless of whether there are updates, consuming more resources.'
      },
      {
        id: 'q3-community-resources',
        question: 'Where can you find community-contributed n8n workflows?',
      options: [
        'Only in paid templates',
        'n8n community forum and template marketplace',
        'They are not available',
        'Only in private repositories'
      ],
      correctAnswer: 1,
      explanation: 'The n8n community actively shares workflows through the official forum, template marketplace, and GitHub, making it easy to learn from real-world examples.'
      }
    ]
  }
};
